// Часть разбиения MapView.vue (фаза 2) - MERGED-composable по явной причине
// (см. план разбиения): три группы кода образуют один сильно связный
// компонент, который нельзя развести по разным файлам -
//   refreshPoiEditSources -> refreshPoiPinMarkers/refreshPoiClusterSource
//   refreshPoiPinMarkers  -> openPoiEditor
// т.е. редактирование POI в режиме карандаша, DOM-пины POI и обновление
import { computed, defineAsyncComponent, ref, watch } from "vue";
import * as maplibregl from "maplibre-gl";
import type { Marker, VectorTileSource } from "maplibre-gl";
import type { Feature } from "geojson";
import api from "../api";
import { useToast } from "./useToast";
import type { PoiEditFeature, PoiOverrideSavePayload } from "../types/poiOverrides";
import {
  BUILDINGS_ADMIN_SOURCE,
  BUILDINGS_SOURCE,
  POIS_ADMIN_SOURCE,
  POIS_CLUSTER_SOURCE,
  POIS_SOURCE,
  vectorTileUrl,
} from "../map/constants";
import {
  createRouteBadgeElement,
  escapeHtml,
  setGeoJsonSourceData,
  shouldShowDetailMarkers,
  upsertPopupContent,
} from "../map/mapUiHelpers";
import type { MapCtx } from "../map/mapContext";
import { isPoiKindVisible } from "../map/poiVisibility";
import { buildPoiIconSvg } from "../map/poiIcons";
import { usePoiLayersStore } from "../store/poiLayers";

// Какие типы POI получают полноценный пин (та же капля, что у узлов/точек
// маршрута) вместо мелкого глифа на карте. Спасслужбы - единственное место
// на карте, где маркер специально ищут взглядом, а не замечают мимоходом.
const POI_PIN_VARIANT: Record<string, string> = {
  mountain_rescue: "danger",
  rescue_hut: "camp",
};
// Пины, у которых глиф - значок вида в общем стиле POI (а не эмодзи) и есть подпись
// под остриём. Подпись у пина нужна, потому что слой подписей карты такие виды не рисует.
const POI_PIN_ICON_KINDS = new Set(["rescue_hut"]);
const PIN_POI_KINDS = Object.keys(POI_PIN_VARIANT);

// Типы, не входящие в кластеризуемую группу пиктограмм (слои
// pois-icon-unclustered-* и DOM-чипы из refreshPoiClusterMarkers, см.
// addBasemapLayers) и в её слой подписей (pois-label): peak/mountain_pass
// рисуются собственным символом, PIN_POI_KINDS - пинами, `information`
// (информационные стенды) скрыт полностью без замены. power_tower тоже
// исключён - по причине LOD, а не захламления (см. отдельный слой
// pois-power-tower ниже); заодно не попадает в pois-label, чтобы не было
// подписи "Опора ЛЭП" под каждой опорой в ряд, как и у peak/mountain_pass.
// `bench` скрыт так же, как `information`, без замены - решение по
// содержанию карты, а не по качеству данных: скамейка просто не то, что
// эта карта должна показывать.
// Экспортируется - нужна и addBasemapLayers (MapView.vue::setupMapLayers, при
// первичном создании слоёв кластера) не только коду внутри этого composable'а.
export const POI_CLUSTER_EXCLUDED_KINDS = [
  ...PIN_POI_KINDS,
  "peak",
  "mountain_pass",
  "information",
  "power_tower",
  "bench",
];


// Только на русском, как и остальные строки попапов на карте - сознательно
// не через t(). Служит заголовком/подписью типа для попапа пина спасслужбы
// (makePoiPinPopupHtml).
const POI_KIND_LABELS_RU: Record<string, string> = {
  peak: "Вершина",
  mountain_pass: "Перевал",
  mountain_rescue: "Спасательная станция",
  rescue_hut: "Спасательная хижина",
  camp_site: "Стоянка",
  alpine_hut: "Альпхижина",
  wilderness_hut: "Хижина",
  viewpoint: "Смотровая площадка",
  shelter: "Навес",
  spring: "Родник",
  drinking_water: "Питьевая вода",
  water_well: "Колодец",
  water_tap: "Водопроводный кран",
  water_point: "Точка воды",
  toilets: "Туалет",
  waste_basket: "Урна",
  waste_disposal: "Мусорный пункт",
  ford: "Брод",
  firepit: "Кострище",
  cable_car_station: "Станция канатной дороги",
  cave_entrance: "Вход в пещеру",
  rock: "Скала",
  waterfall: "Водопад",
  tower: "Вышка",
  power_tower: "Опора ЛЭП",
  bench: "Скамья",
  picnic_table: "Стол для пикника",
  information: "Информационный стенд",
};

// Все водные типы переиспользуют ту же PNG-каплю, что и вариант "water" у
// маркеров маршрута (createRouteBadgeElement) - единая иконка, попап всё
// равно указывает конкретный OSM-тег в строке типа. У спасслужбы своей
// иконки нет, поэтому она идёт по пути с обычным глифом.
const createPoiPinElement = (kind: string, name: string) => {
  const variant = POI_PIN_VARIANT[kind] ?? "default";
  const label = name || POI_KIND_LABELS_RU[kind] || "";
  const el = createRouteBadgeElement(variant, "🆘", label, null);
  if (POI_PIN_ICON_KINDS.has(kind)) {
    const pin = el.querySelector<HTMLElement>(".route-map-pin");
    const glyph = pin?.querySelector<HTMLElement>(".route-map-pin__glyph");
    const svg = buildPoiIconSvg(kind, "currentColor");
    if (glyph && svg) glyph.innerHTML = svg;
    if (pin && label) {
      const span = document.createElement("span");
      span.className = "route-map-pin__label";
      span.textContent = label;
      pin.appendChild(span);
    }
  }
  return el;
};

const makePoiPinPopupHtml = (kind: string, name: string) => {
  const label = POI_KIND_LABELS_RU[kind] || "";
  return `
    <div class="poi-pin-popup">
      <p class="poi-pin-popup__title">${escapeHtml(name || label)}</p>
      ${name && label ? `<p class="poi-pin-popup__kind">${escapeHtml(label)}</p>` : ""}
    </div>
  `;
};

// JS-двойник цепочки coalesce из buildNameField (frontend/src/map/labels.ts)
// для обработчиков клика, читающих результат querySourceFeatures напрямую,
// минуя style-выражение - тот же приоритет языка, просто вычисляется один
// раз над реальными значениями свойств.
const pickFeatureName = (props: Record<string, unknown>, lang: string): string => {
  const localized = lang === "en" ? undefined : props[`name_${lang}`];
  return String(localized || props.name_en || props.name || "").trim();
};

// origin+source_id (20260829_05_pois_tile_overrides.py) - стабильный id на
// каждом объекте pois/pois_admin MVT, используется вместо хэша координат.
// Координаты остаются фолбэком только на случай (не должен наступать),
// когда одного из полей нет.
const poiPinKey = (kind: string, lng: number, lat: number, origin?: unknown, sourceId?: unknown) =>
  origin != null && sourceId != null
    ? `${origin}:${sourceId}`
    : `${kind}:${lng.toFixed(5)}:${lat.toFixed(5)}`;

export const EDIT_POINT_LAYERS = [
  "pois-peak-admin",
  "pois-pass-admin",
  "pois-icon-unclustered",
  "buildings-label-admin",
] as const;

// Тап по точке на карте вне режима редактирования открывает её карточку
// (MapView emit point-tap -> App.vue ?point=). Только отдельные точки: чипы
// кластеров (pois-clusters) по-прежнему холостые. Места (place:<id>) слоями
// POI не рисуются.
export const POI_TAP_LAYERS = ["pois-icon-unclustered", "pois-peak", "pois-pass"] as const;
// Полуразмер рамки вокруг пальца для queryRenderedFeatures (иконки мелкие).
const POI_TAP_HIT_PX = 6;

export interface UsePoiLayersOptions {
  getLabelLang: () => string;
  // id точки в формате бэкенда: "osm:<node_id>" / "hiking:<id>".
  onPointTap?: (id: string) => void;
}

export function usePoiLayers(ctx: MapCtx, options: UsePoiLayersOptions) {
  // POI спасслужб рисуются пинами (PIN_POI_KINDS), как узлы/точки маршрута, а
  // не глиппами базовой карты - ключ по kind+округлённым координатам, так как
  // pois_tile не отдаёт стабильный id объекта для диффа (см. refreshPoiPinMarkers).
  const poiPinMarkers = new Map<string, Marker>();
  let poiClusterPayload = "";
  let poiEditRefreshGeneration = 0;
  let cancelPoiEditRefresh: (() => void) | null = null;

  const clearPoiPinMarkers = () => {
    poiPinMarkers.forEach((marker) => marker.remove());
    poiPinMarkers.clear();
  };

  // ── Расширение POI-кластера ─────────────────────────────────────────────
  // Сам клик-для-зума теперь на DOM-чипе (zoomIntoPoiCluster ниже) - тут
  // остались только два курсорных хелпера, на которые всё ещё завязан
  // EDIT_POINT_LAYERS.
  const setClusterCursorPointer = () => {
    const map = ctx.getMap();
    if (map) map.getCanvas().style.cursor = "pointer";
  };
  const clearClusterCursorPointer = () => {
    const map = ctx.getMap();
    if (map) map.getCanvas().style.cursor = "";
  };

  // ── Редактирование POI в режиме редактирования карты ────────────────────
  const { message: editorToastMessage, show: showEditorToast } = useToast();

  // Ленивая загрузка только при включении режима редактирования - Element
  // Plus не попадает в публичный бандл карты (см. CLAUDE.md про Element Plus).
  const PoiOverrideEditor = defineAsyncComponent(() => import("../admin/PoiOverrideEditor.vue"));
  const editorOpen = ref(false);
  const editorSaving = ref(false);
  const editorFeature = ref<PoiEditFeature | null>(null);
  const editorScreen = ref<{ x: number; y: number } | null>(null);

  const closeEditor = () => {
    editorOpen.value = false;
    editorFeature.value = null;
    editorScreen.value = null;
  };

  const handleEditorEscape = (e: KeyboardEvent) => {
    if (e.key === "Escape" && editorOpen.value) closeEditor();
  };

  // Экранная позиция следует за картой, пока попап открыт - пересчитывается на
  // каждый move/zoom, а не фиксируется один раз; MapLibre-Popup делает это
  // автоматически, а этот самодельный оверлей - вручную (editorPopupStyle).
  const updateEditorScreenPos = () => {
    const map = ctx.getMap();
    if (!map || !editorFeature.value) return;
    const p = map.project([editorFeature.value.lng, editorFeature.value.lat]);
    editorScreen.value = { x: p.x, y: p.y };
  };

  const editorPopupStyle = computed(() => {
    if (!editorScreen.value) return { display: "none" };
    return {
      position: "absolute" as const,
      left: `${editorScreen.value.x}px`,
      top: `${editorScreen.value.y}px`,
      transform: "translate(-50%, -100%)",
      zIndex: 30,
    };
  });

  // Читает прямо из свойств кликнутого объекта - и pois_tile_admin (MVT), и
  // payload POIS_CLUSTER_SOURCE в режиме редактирования (refreshPoiClusterSource)
  // уже несут все нужные форме поля, отдельный GET перед рендером попапа не
  // нужен (см. также раздел poi_overrides в api-reference.md).
  const openPoiEditor = (props: Record<string, unknown>, lngLat: [number, number]) => {
    if (!ctx.getMap()) return;
    const origin = props.origin === "hiking" ? "hiking" : props.origin === "building" ? "building" : "osm";
    const sourceId = Number(props.source_id);
    if (!Number.isFinite(sourceId)) return;
    editorFeature.value = {
      origin,
      sourceId,
      kind: String(props.kind ?? ""),
      lng: lngLat[0],
      lat: lngLat[1],
      nameRu: (props.name_ru as string) ?? null,
      nameEn: (props.name_en as string) ?? null,
      nameKk: (props.name_kk as string) ?? null,
      baseName: (props.name as string) ?? null,
      hidden: props.hidden === true,
      pinned: props.pinned === true,
      ele: typeof props.ele === "number" ? props.ele : null,
      difficulty: (props.difficulty as string) ?? null,
      hasOverride: props.has_override === true,
    };
    updateEditorScreenPos();
    editorOpen.value = true;
  };

  // Слушатели, привязанные к слою (map.on(event, layerId, handler)), а не
  // глобальный обработчик с фильтром - "режим редактирования не трогает линии
  // и полигоны" структурно гарантировано списком EDIT_POINT_LAYERS. В нём есть
  // buildings-label-admin, чья геометрия - полигон здания (symbol-placement:
  // "point" меняет только размещение глифа, не тип геометрии), поэтому якорь
  // попапа для не-Point объектов берётся из точки клика (e.lngLat), а не из
  // координат фичи.
  const handlePoiEditClick = (e: maplibregl.MapLayerMouseEvent) => {
    if (!ctx.editMode.value || !ctx.getMap()) return;
    const feature = e.features?.[0];
    if (!feature) return;
    e.preventDefault();
    const anchor: [number, number] =
      feature.geometry.type === "Point"
        ? (feature.geometry.coordinates as [number, number])
        : [e.lngLat.lng, e.lngLat.lat];
    openPoiEditor(feature.properties ?? {}, anchor);
  };

  // Из общего обработчика клика карты (map/mapClick.ts): true - клик забран
  // точкой (точка важнее линии маршрута под ней). В режиме редактирования -
  // всегда false: там клик по точке открывает редактор (handlePoiEditClick).
  const handlePointTap = (e: maplibregl.MapMouseEvent): boolean => {
    const map = ctx.getMap();
    if (!map || ctx.editMode.value || !options.onPointTap) return false;
    // Клики по DOM-маркерам/попапам всплывают сюда же - точкой считаем только
    // попадание по самому canvas (как handleMapClickForCursor).
    const target = e.originalEvent?.target as HTMLElement | null | undefined;
    if (target && target !== map.getCanvas()) return false;
    const layers = POI_TAP_LAYERS.filter((id) => map.getLayer(id));
    if (!layers.length) return false;
    const { x, y } = e.point;
    const features = map.queryRenderedFeatures(
      [
        [x - POI_TAP_HIT_PX, y - POI_TAP_HIT_PX],
        [x + POI_TAP_HIT_PX, y + POI_TAP_HIT_PX],
      ],
      { layers }
    );
    for (const f of features) {
      const props = f.properties ?? {};
      if ((props.origin !== "osm" && props.origin !== "hiking") || props.source_id == null || props.source_id === "") continue;
      options.onPointTap(`${props.origin}:${props.source_id}`);
      return true;
    }
    return false;
  };

  const handleEditorSave = async (payload: PoiOverrideSavePayload) => {
    const feature = editorFeature.value;
    if (!feature) return;
    editorSaving.value = true;
    try {
      await api.put(`/admin/poi-overrides/${feature.origin}/${feature.sourceId}`, {
        name_ru: payload.nameRu || null,
        name_en: payload.nameEn || null,
        name_kk: payload.nameKk || null,
        hidden: payload.hidden,
        pinned: payload.pinned,
        ele: payload.ele,
        difficulty: payload.difficulty || null,
      });
      showEditorToast(ctx.t("map.editSaved"));
      closeEditor();
      refreshPoiEditSources();
    } catch {
      showEditorToast(ctx.t("map.editSaveError"));
    } finally {
      editorSaving.value = false;
    }
  };

  const handleEditorRevert = async () => {
    const feature = editorFeature.value;
    if (!feature) return;
    editorSaving.value = true;
    try {
      await api.delete(`/admin/poi-overrides/${feature.origin}/${feature.sourceId}`);
      showEditorToast(ctx.t("map.editReverted"));
      closeEditor();
      refreshPoiEditSources();
    } catch {
      showEditorToast(ctx.t("map.editSaveError"));
    } finally {
      editorSaving.value = false;
    }
  };

  // Пересобирает набор пинов из уже загруженных тайлов POIS_SOURCE (или
  // POIS_ADMIN_SOURCE в режиме редактирования) - querySourceFeatures видит
  // только тайлы, уже в памяти, поэтому дёшево и лениво само по себе.
  // Запускается по 'idle' (после того как тайлы/рендер осядут после
  // пана/зума), а не по 'moveend', чтобы не работать с тайлами в полёте.
  //
  // Учитывает любой объект с `pinned`, а не только PIN_POI_KINDS - переключатель
  // «Отображать как булавку» в редакторе POI (PoiOverrideEditor.vue) заставляет
  // точку рендериться так вне зависимости от её типа.
  const refreshPoiPinMarkers = () => {
    const map = ctx.getMap();
    if (!map || !ctx.isMapReady()) return;
    if (!shouldShowDetailMarkers(map)) {
      clearPoiPinMarkers();
      return;
    }
    const sourceId = ctx.editMode.value ? POIS_ADMIN_SOURCE : POIS_SOURCE;
    if (!map.isSourceLoaded(sourceId)) return;
    let features: maplibregl.GeoJSONFeature[] = [];
    try {
      features = map.querySourceFeatures(sourceId, {
        sourceLayer: sourceId,
        filter: ["any", ["match", ["get", "kind"], PIN_POI_KINDS, true, false], ["==", ["get", "pinned"], true]],
      });
    } catch {
      return;
    }
    const seen = new Set<string>();
    for (const feature of features) {
      if (feature.geometry?.type !== "Point") continue;
      const [lng, lat] = feature.geometry.coordinates as [number, number];
      const props = feature.properties ?? {};
      const kind = String(props.kind ?? "");
      if (!kind) continue;
      const key = poiPinKey(kind, lng, lat, props.origin, props.source_id);
      // Точка на стыке тайлов возвращается по разу на каждый покрывающий
      // тайл - дедуп по тому же ключу, не просто пропуск повторов между вызовами.
      if (seen.has(key)) continue;
      seen.add(key);
      if (poiPinMarkers.has(key)) continue;
      const name = pickFeatureName(props, options.getLabelLang());
      const el = createPoiPinElement(kind, name);
      if (ctx.editMode.value && props.hidden === true) el.classList.add("route-map-marker-icon--hidden");
      if (ctx.editMode.value && props.recently_changed === true) el.classList.add("route-map-marker-icon--recent");
      const marker = new maplibregl.Marker({ element: el, anchor: "bottom" }).setLngLat([lng, lat]);
      if (ctx.editMode.value) {
        el.addEventListener("click", (ev) => {
          ev.stopPropagation();
          openPoiEditor(props, [lng, lat]);
        });
      } else {
        upsertPopupContent(marker, makePoiPinPopupHtml(kind, name), ctx);
      }
      marker.addTo(map);
      poiPinMarkers.set(key, marker);
    }
    poiPinMarkers.forEach((marker, key) => {
      if (!seen.has(key)) {
        marker.remove();
        poiPinMarkers.delete(key);
      }
    });
  };

  // Питает POIS_CLUSTER_SOURCE из уже загруженных тайлов POIS_SOURCE (или
  // POIS_ADMIN_SOURCE в режиме редактирования) - тот же ленивый подход в
  // пределах вьюпорта, что и у refreshPoiPinMarkers, только данные отдаются
  // кластеризатору MapLibre (supercluster), а не диффятся напрямую в DOM-маркеры;
  // refreshPoiClusterMarkers (ниже) - потребитель, который читает обратно
  // структуру кластеров/листьев этого источника для чипов. POI_LOD_MIN_ZOOM
  // (жёсткий порог, проверяется вручную в refreshPoiClusterMarkers) - единственный
  // зум-гейт, нужный этой группе.
  //
  // `pinned`-объекты тоже исключены - они рендерятся пином (refreshPoiPinMarkers),
  // никогда обоими способами сразу.
  const poiLayersStore = usePoiLayersStore();
  const refreshPoiClusterSource = () => {
    const map = ctx.getMap();
    if (!map || !ctx.isMapReady()) return;
    const sourceId = ctx.editMode.value ? POIS_ADMIN_SOURCE : POIS_SOURCE;
    if (!map.isSourceLoaded(sourceId)) return;
    let features: maplibregl.GeoJSONFeature[] = [];
    try {
      features = map.querySourceFeatures(sourceId, {
        sourceLayer: sourceId,
        filter: ["all", ["match", ["get", "kind"], POI_CLUSTER_EXCLUDED_KINDS, false, true], ["!=", ["get", "pinned"], true]],
      });
    } catch {
      return;
    }
    const seen = new Set<string>();
    const pointEntries: Array<{ key: string; feature: Feature }> = [];
    const zoom = map.getZoom();
    const enabledKinds = poiLayersStore.enabledKinds;
    for (const feature of features) {
      if (feature.geometry?.type !== "Point") continue;
      const [lng, lat] = feature.geometry.coordinates as [number, number];
      const props = feature.properties ?? {};
      const kind = String(props.kind ?? "");
      if (!kind) continue;
      // Приоритет по зуму/выбору пользователя - до кластеризации, иначе
      // скрытые виды раздувают чипы (см. map/poiVisibility.ts). В режиме
      // редактирования показываем всё: админ правит и скрытые виды.
      if (!ctx.editMode.value && !isPoiKindVisible(kind, zoom, enabledKinds)) continue;
      // Тот же дубль на стыке тайлов, что и в refreshPoiPinMarkers.
      const key = poiPinKey(kind, lng, lat, props.origin, props.source_id);
      if (seen.has(key)) continue;
      seen.add(key);
      // Имя нужно всегда (не только редактору) - handleClusterHover ниже
      // читает его через getClusterLeaves.
      // origin/source_id - id точки для тапа (handlePointTap), не только редактору.
      const properties: Record<string, unknown> = {
        kind,
        origin: props.origin ?? null,
        source_id: props.source_id ?? null,
        name_ru: props.name_ru ?? null,
        name_en: props.name_en ?? null,
        name_kk: props.name_kk ?? null,
        name: props.name ?? null,
      };
      if (ctx.editMode.value) {
        properties.hidden = props.hidden === true;
        properties.recently_changed = props.recently_changed === true;
        properties.has_override = props.has_override === true;
        properties.ele = props.ele ?? null;
        properties.difficulty = props.difficulty ?? null;
      }
      pointEntries.push({
        key,
        feature: {
          type: "Feature",
          geometry: { type: "Point", coordinates: [lng, lat] },
          properties,
        },
      });
    }
    const points = pointEntries.sort((a, b) => a.key.localeCompare(b.key)).map(({ feature }) => feature);
    const payload = JSON.stringify(points);
    if (payload === poiClusterPayload) return;
    poiClusterPayload = payload;
    setGeoJsonSourceData(map, POIS_CLUSTER_SOURCE, points);
  };
  watch(() => poiLayersStore.enabledGroups, () => refreshPoiClusterSource());

  // Клик по кластеру больше не зумит - обычный жест зума работает как
  // обычно, кластер распадается сам на clusterMaxZoom (basemapLayers.ts).

  // ── Хинты с названиями ───────────────────────────────────────────────────
  // Один переиспользуемый Popup, как showCursorTooltip в useRouteCursor.ts.
  let hoverTooltip: maplibregl.Popup | null = null;
  // Кластерный хинт асинхронный (getClusterLeaves) - generation защищает от
  // того, что более ранний, но дольше резолвящийся ответ перезапишет хинт
  // после того, как мышь уже ушла на другой кластер.
  let hoverTooltipGeneration = 0;
  const CLUSTER_HOVER_LEAF_LIMIT = 40;

  // Разный визуальный радиус у иконки и у кластерного чипа - остриё
  // указателя должно гаситься об край объекта, не в стороне и не внутри.
  const POI_HOVER_OFFSET: [number, number] = [0, -14];
  const CLUSTER_HOVER_OFFSET: [number, number] = [0, -20];

  const ensureHoverTooltip = (lngLat: maplibregl.LngLatLike, offset: [number, number]): maplibregl.Popup => {
    if (!hoverTooltip) {
      hoverTooltip = new maplibregl.Popup({
        closeButton: false,
        closeOnClick: false,
        closeOnMove: false,
        anchor: "bottom",
        className: "poi-hover-tooltip",
        focusAfterOpen: false,
      });
    }
    hoverTooltip.setOffset(offset);
    hoverTooltip.setLngLat(lngLat);
    return hoverTooltip;
  };

  const hidePoiHoverTooltip = () => {
    hoverTooltipGeneration += 1;
    hoverTooltip?.remove();
  };

  // Хинт называет ВИД объекта (не OSM-имя - то уже показывает pois-label).
  const featureKindLabel = (props: Record<string, unknown>): string => {
    const kind = String(props.kind ?? "");
    return POI_KIND_LABELS_RU[kind] || kind;
  };

  const handlePoiHover = (event: maplibregl.MapLayerMouseEvent) => {
    const map = ctx.getMap();
    const feature = event.features?.[0];
    if (!map || feature?.geometry.type !== "Point") return;
    if (ctx.isRulerActive()) {
      hidePoiHoverTooltip();
      return;
    }
    const label = featureKindLabel(feature.properties ?? {});
    if (!label) {
      hidePoiHoverTooltip();
      return;
    }
    hoverTooltipGeneration += 1;
    ensureHoverTooltip(feature.geometry.coordinates as [number, number], POI_HOVER_OFFSET).setText(label).addTo(map);
  };

  const handleClusterHover = (event: maplibregl.MapLayerMouseEvent) => {
    const map = ctx.getMap();
    const source = map?.getSource(POIS_CLUSTER_SOURCE) as maplibregl.GeoJSONSource | undefined;
    const feature = event.features?.[0];
    if (!map || !source || feature?.geometry.type !== "Point") return;
    if (ctx.isRulerActive()) {
      hidePoiHoverTooltip();
      return;
    }
    const clusterId = Number(feature.properties?.cluster_id);
    if (!Number.isFinite(clusterId)) return;
    const lngLat = feature.geometry.coordinates as [number, number];
    const generation = ++hoverTooltipGeneration;
    source
      .getClusterLeaves(clusterId, CLUSTER_HOVER_LEAF_LIMIT, 0)
      .then((leaves) => {
        if (generation !== hoverTooltipGeneration || !ctx.getMap()) return;
        const counts = new Map<string, number>();
        for (const leaf of leaves) {
          const label = featureKindLabel(leaf.properties ?? {});
          if (label) counts.set(label, (counts.get(label) ?? 0) + 1);
        }
        const lines = [...counts.entries()]
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([label, count]) => escapeHtml(label) + (count > 1 ? ` <span class="poi-hint-count">×${count}</span>` : ""));
        if (lines.length === 0) return;
        const currentMap = ctx.getMap();
        if (!currentMap) return;
        ensureHoverTooltip(lngLat, CLUSTER_HOVER_OFFSET).setHTML(lines.join("<br>")).addTo(currentMap);
      })
      .catch(() => {});
  };

  // Перестраивает весь POI-рендеринг, зависящий от режима редактирования,
  // после сохранения/удаления.
  //
  // Сбрасывается кэш и у POIS_SOURCE, и у POIS_ADMIN_SOURCE, не только у
  // admin-источника - у nginx свой кэш и на публичном `pois`, он так же
  // протухает. Это важно: выход из режима редактирования сразу после
  // сохранения переключает видимые слои peak/pass обратно на
  // `pois-peak`/`pois-pass` (POIS_SOURCE), и если обновить только admin-источник,
  // правка будет выглядеть "не сохранившейся".
  //
  // Query-параметр `_edited` явно обходит чтение и запись proxy-cache nginx
  // для этого перезапроса, не трогая TTL остальных запросов. Этого одного
  // было недостаточно: у martin (за nginx) свой in-process кэш тайлов
  // по (source, z, x, y) без учёта
  // query-строки и без ручки инвалидации, так что сброс кэша на уровне nginx
  // упирался в устаревшие байты из кэша martin. Поэтому кэш martin отключён
  // целиком (`--cache-size 0`, см. сервис martin в docker-compose.yml).
  const refreshPoiEditSources = () => {
    const map = ctx.getMap();
    if (!map) return;
    cancelPoiEditRefresh?.();
    const generation = ++poiEditRefreshGeneration;
    const targetSourceId = ctx.editMode.value ? POIS_ADMIN_SOURCE : POIS_SOURCE;
    let reloadStarted = false;
    let settled = false;
    let timeoutId: number | null = null;
    const cleanup = () => {
      map.off("sourcedataloading", onSourceLoading);
      map.off("sourcedata", onSourceData);
      if (timeoutId != null) window.clearTimeout(timeoutId);
      if (cancelPoiEditRefresh === cleanup) cancelPoiEditRefresh = null;
    };
    const rebuildDerivedLayers = () => {
      if (settled || generation !== poiEditRefreshGeneration) return;
      settled = true;
      cleanup();
      clearPoiPinMarkers();
      refreshPoiPinMarkers();
      refreshPoiClusterSource();
    };
    const onSourceLoading = (e: maplibregl.MapSourceDataEvent) => {
      if (e.sourceId === targetSourceId && generation === poiEditRefreshGeneration) reloadStarted = true;
    };
    const onSourceData = (e: maplibregl.MapSourceDataEvent) => {
      if (e.sourceId !== targetSourceId || settled || generation !== poiEditRefreshGeneration) return;
      if (e.sourceDataType === "content" && e.sourceDataChanged === true) reloadStarted = true;
      if (!reloadStarted || !e.isSourceLoaded) return;
      if (!e.tile && e.sourceDataType !== "idle") return;
      rebuildDerivedLayers();
    };
    map.on("sourcedataloading", onSourceLoading);
    map.on("sourcedata", onSourceData);
    cancelPoiEditRefresh = cleanup;

    // Date.now() сохраняет уникальность URL между загрузками страницы, чтобы
    // браузер не вернул предыдущий тайл из своего 60-секундного кэша.
    const cacheBust = Date.now();
    const publicSource = map.getSource(POIS_SOURCE) as VectorTileSource | undefined;
    publicSource?.setTiles([`${vectorTileUrl(POIS_SOURCE)}?_edited=${cacheBust}`]);
    const adminSource = map.getSource(POIS_ADMIN_SOURCE) as VectorTileSource | undefined;
    adminSource?.setTiles([`${vectorTileUrl(POIS_ADMIN_SOURCE)}?_edited=${cacheBust}`]);
    const buildingsSource = map.getSource(BUILDINGS_SOURCE) as VectorTileSource | undefined;
    buildingsSource?.setTiles([`${vectorTileUrl(BUILDINGS_SOURCE)}?_edited=${cacheBust}`]);
    const buildingsAdminSource = map.getSource(BUILDINGS_ADMIN_SOURCE) as VectorTileSource | undefined;
    buildingsAdminSource?.setTiles([`${vectorTileUrl(BUILDINGS_ADMIN_SOURCE)}?_edited=${cacheBust}`]);
    // MapLibre может сообщить metadata со старым isSourceLoaded=true до начала
    // reload. Поэтому фиксируем sourcedataloading и ждём загруженный тайл или
    // idle; при таймауте оставляем прежние маркеры на карте.
    timeoutId = window.setTimeout(() => {
      if (generation !== poiEditRefreshGeneration) return;
      settled = true;
      cleanup();
    }, 4000);
  };

  const setLayerVisible = (layerId: string, visible: boolean) => {
    const map = ctx.getMap();
    if (!map || !map.getLayer(layerId)) return;
    map.setLayoutProperty(layerId, "visibility", visible ? "visible" : "none");
  };

  const toggleEditMode = () => {
    const map = ctx.getMap();
    if (!map) return;
    ctx.editMode.value = !ctx.editMode.value;
    closeEditor();
    const showAdmin = ctx.editMode.value;
    setLayerVisible("pois-peak", !showAdmin);
    setLayerVisible("pois-pass", !showAdmin);
    setLayerVisible("pois-peak-admin", showAdmin);
    setLayerVisible("pois-pass-admin", showAdmin);
    setLayerVisible("pois-peak-recent-ring", showAdmin);
    setLayerVisible("pois-pass-recent-ring", showAdmin);
    setLayerVisible("buildings-label", !showAdmin);
    setLayerVisible("buildings-label-admin", showAdmin);
    refreshPoiEditSources();
  };

  // Используется только при полном размонтировании MapView (см. onBeforeUnmount).
  const destroy = () => {
    poiEditRefreshGeneration += 1;
    cancelPoiEditRefresh?.();
    clearPoiPinMarkers();
    hoverTooltip?.remove();
    hoverTooltip = null;
  };

  return {
    PoiOverrideEditor,
    editorOpen,
    editorSaving,
    editorFeature,
    editorScreen,
    editorToastMessage,
    editorPopupStyle,
    closeEditor,
    handleEditorEscape,
    updateEditorScreenPos,
    handlePoiEditClick,
    handlePointTap,
    handleEditorSave,
    handleEditorRevert,
    toggleEditMode,
    setClusterCursorPointer,
    clearClusterCursorPointer,
    handlePoiHover,
    handleClusterHover,
    hidePoiHoverTooltip,
    refreshPoiPinMarkers,
    refreshPoiClusterSource,
    destroy,
  };
}
